<script setup lang="ts">
import { relinkBackgrounds } from "~/assets/images/relink/assets";

const route = useRoute();
const backTo = computed(() => route.meta.backTo as string | undefined);
const background = computed(
  () =>
    (route.meta.background as "default" | "profile" | undefined) ?? "default"
);
const panel = computed(
  () => (route.meta.panel as "card" | "plain" | undefined) ?? "card"
);
const backgroundAsset = computed(() =>
  relinkBackgrounds.find((asset) =>
    background.value === "profile"
      ? asset.slug === "registration-profile"
      : asset.slug === "auth-desktop"
  )
);
const { t } = useI18n();
</script>

<template>
  <main
    class="auth-layout"
    :class="[`auth-layout--${background}`, `auth-layout--${panel}`]"
  >
    <DesignSystemNotificationSection top-size="small" />

    <div
      v-if="backgroundAsset"
      class="auth-layout__background"
      :style="{ backgroundImage: `url(${backgroundAsset.src})` }"
      aria-hidden="true"
    />

    <NuxtLink v-if="backTo" class="auth-layout__back" :to="backTo">
      <DesignSystemIcon icon="chevron-left" aria-hidden="true" />
      {{ t("common.back") }}
    </NuxtLink>

    <section class="auth-layout__panel">
      <slot />
    </section>
  </main>
</template>

<style scoped lang="scss">
.auth-layout {
  position: relative;
  display: grid;
  min-height: 100dvh;
  place-items: center;
  overflow-x: hidden;
  overflow-y: auto;
  background: $background-auth;
  color: $tertiary;
  font-family: $relink-font-family-primary;
  padding: 24px;
}

.auth-layout__background {
  position: absolute;
  inset: 0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.auth-layout__back {
  position: fixed;
  z-index: 3;
  top: 31px;
  left: 56px;
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  gap: 7px;
  border-radius: 999px;
  background: $white;
  color: $text-auth-link;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  text-decoration: none;
  padding: 8px 16px 8px 13px;
}

.auth-layout__panel {
  position: relative;
  z-index: 1;
  display: grid;
  width: min(100%, 640px);
  align-content: start;
  border-radius: 16px;
  background: $white;
  box-shadow: 0 10px 18px rgba($tertiary, 0.18);
  padding: 47px 48px 48px;
}

.auth-layout--profile .auth-layout__background {
  inset: 24px 24px auto;
  height: 392px;
  border-radius: 24px;
  background-size: 100% 100%;
}

.auth-layout--profile .auth-layout__panel {
  width: min(100%, 546px);
  padding: 56px;
}

.auth-layout--plain .auth-layout__panel {
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

@media (width <= 680px) {
  .auth-layout {
    align-items: start;
    padding: 88px 16px 24px;
  }

  .auth-layout__back {
    top: 20px;
    left: 16px;
    font-size: 14px;
  }

  .auth-layout__panel {
    padding: 40px 24px;
  }

  .auth-layout--profile .auth-layout__background {
    display: none;
  }

  .auth-layout--profile .auth-layout__panel {
    width: min(100%, 640px);
  }

  .auth-layout--plain .auth-layout__panel {
    padding: 0 16px;
  }
}
</style>
